import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Observable } from 'rxjs';
import { DevExEditorComponent, EditorToolbarItem } from '../../../devExtreme/src/devExEditor/devExEditor.component';
import { ComponentStyle, IAbcComponent } from '../abcComponent.model';
import { AbcComponentService } from '../abcComponent.service';
import {
  EditorStyle,
  EditorValidationMessageMode,
  EditorValidationStatus,
  IEditor,
  StylingModeEditor,
} from './abcEditor';

@Component({
  selector: 'abcEditor',
  template: '<ng-template #abcEditor></ng-template>',
})
export class AbcEditorComponent implements IAbcComponent, OnInit, AfterViewInit, OnDestroy {
  @ViewChild('abcEditor', { static: true, read: ViewContainerRef }) viewContainerRef: ViewContainerRef;
  @Input('id') attrId: string;
  @Output() onValueChanged = new EventEmitter();
  @Output() onEdit = new EventEmitter();
  @Output() onSaveClick = new EventEmitter();
  @Input() filterToolbarItems: (items: EditorToolbarItem[]) => EditorToolbarItem[];
  get id(): string {
    return this.componentRef.id;
  }
  @Input('tools') attrTools: string;
  @Input() private style: ComponentStyle;
  public htmlAttributes: IEditor;
  private eventListener = [];
  private componentRef;
  private _height: string | number;
  private _width: string | number;
  private _disabled: boolean;
  private _activeStateEnabled: boolean;
  private _focusStateEnabled: boolean;
  private _displayEdit: boolean;
  private _showToolbar: boolean;
  private _hoverStateEnabled: boolean;
  private _isValid: boolean;
  private _name: string;
  private _placeholder: string;
  private _readOnly: boolean;
  private _displayIconEdit: boolean;
  private _rtlEnabled: boolean;
  private _tabIndex: number;
  private _validationError: string;
  private _validationErrors: string;
  private _validationMessageMode: EditorValidationMessageMode;
  private _validationStatus: EditorValidationStatus;
  private _value: string;
  private _visible: boolean;
  private _class: string;
  private _saveIcon: boolean;
  private _identifier: string;
  private _stylingMode: StylingModeEditor;

  @Input()
  get height() {
    return this._height;
  }
  set height(data: string | number) {
    this._height = data;
    if (this.componentRef) {
      this.componentRef.height = data;
    }
  }

  @Input()
  get width() {
    return this._width;
  }
  set width(data: string | number) {
    this._width = data;
    if (this.componentRef) {
      this.componentRef.width = data;
    }
  }

  @Input()
  get stylingMode() {
    return this._stylingMode;
  }
  set stylingMode(data: StylingModeEditor) {
    this._stylingMode = data;
    if (this.componentRef) {
      this.componentRef.stylingMode = data;
    }
  }

  @Input()
  get saveIcon() {
    return this._saveIcon;
  }
  set saveIcon(data: boolean) {
    this._saveIcon = data;
    if (this.componentRef) {
      this.componentRef.saveIcon = data;
    }
  }

  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(data: boolean) {
    this._disabled = data;
    if (this.componentRef) {
      this.componentRef.disabled = data;
    }
  }

  @Input()
  get showToolbar() {
    return this._showToolbar;
  }
  set showToolbar(data: boolean) {
    this._showToolbar = data;
    if (this.componentRef) {
      this.componentRef.showToolbar = data;
    }
  }

  @Input()
  get displayEdit() {
    return this._displayEdit;
  }
  set displayEdit(data: boolean) {
    this._displayEdit = data;
    if (this.componentRef) {
      this.componentRef.displayEdit = data;
    }
  }

  @Input()
  get activeStateEnabled() {
    return this._activeStateEnabled;
  }
  set activeStateEnabled(data: boolean) {
    this._activeStateEnabled = data;
    if (this.componentRef) {
      this.componentRef.activeStateEnabled = data;
    }
  }

  @Input()
  get focusStateEnabled() {
    return this._focusStateEnabled;
  }
  set focusStateEnabled(data: boolean) {
    this._focusStateEnabled = data;
    if (this.componentRef) {
      this.componentRef.focusStateEnabled = data;
    }
  }

  @Input()
  get hoverStateEnabled() {
    return this._hoverStateEnabled;
  }
  set hoverStateEnabled(data: boolean) {
    this._hoverStateEnabled = data;
    if (this.componentRef) {
      this.componentRef.hoverStateEnabled = data;
    }
  }

  @Input()
  get isValid() {
    return this._isValid;
  }
  set isValid(data: boolean) {
    this._isValid = data;
    if (this.componentRef) {
      this.componentRef.isValid = data;
    }
  }

  @Input()
  get name() {
    return this._name;
  }
  set name(data: string) {
    this._name = data;
    if (this.componentRef) {
      this.componentRef.name = data;
    }
  }

  @Input()
  get readOnly() {
    return this._readOnly;
  }
  set readOnly(data: boolean) {
    this._readOnly = data;
    if (this.componentRef) {
      this.componentRef.readOnly = data;
    }
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(data: string) {
    this._placeholder = data;
    if (this.componentRef) {
      this.componentRef.placeholder = data;
    }
  }

  @Input()
  get displayIconEdit() {
    return this._displayIconEdit;
  }
  set displayIconEdit(data: boolean) {
    this._displayIconEdit = data;
    if (this.componentRef) {
      this.componentRef.displayIconEdit = data;
    }
  }

  @Input()
  get class() {
    return this._class;
  }
  set class(data: string) {
    this._class = data;
    if (this.componentRef) {
      this.componentRef.class = data;
    }
  }

  @Input()
  get rtlEnabled() {
    return this._rtlEnabled;
  }
  set rtlEnabled(data: boolean) {
    this._rtlEnabled = data;
    if (this.componentRef) {
      this.componentRef.rtlEnabled = data;
    }
  }

  @Input()
  get tabIndex() {
    return this._tabIndex;
  }
  set tabIndex(data: number) {
    this._tabIndex = data;
    if (this.componentRef) {
      this.componentRef.tabIndex = data;
    }
  }

  @Input()
  get validationError() {
    return this._validationError;
  }
  set validationError(data: string) {
    this._validationError = data;
    if (this.componentRef) {
      this.componentRef.validationError = data;
    }
  }

  @Input()
  get validationErrors() {
    return this._validationErrors;
  }
  set validationErrors(data: string) {
    this._validationErrors = data;
    if (this.componentRef) {
      this.componentRef.validationErrors = data;
    }
  }

  @Input()
  get validationMessageMode() {
    return this._validationMessageMode;
  }
  set validationMessageMode(data: EditorValidationMessageMode) {
    this._validationMessageMode = data;
    if (this.componentRef) {
      this.componentRef.validationMessageMode = data;
    }
  }

  @Input()
  get validationStatus() {
    return this._validationStatus;
  }
  set validationStatus(data: EditorValidationStatus) {
    this._validationStatus = data;
    if (this.componentRef) {
      this.componentRef.validationStatus = data;
    }
  }

  @Input()
  get value() {
    return this._value;
  }
  set value(data: string) {
    this._value = data;
    if (this.componentRef) {
      this.componentRef.value = data;
    }
  }

  @Input()
  get visible() {
    return this._visible;
  }
  set visible(data: boolean) {
    this._visible = data;
    if (this.componentRef) {
      this.componentRef.visible = data;
    }
  }

  constructor(private abcComponentService: AbcComponentService) {
    this.style = ComponentStyle.DevExpress;
    this.rtlEnabled = this.abcComponentService.rtlEnabled;
  }

  ngOnInit() {
    this.setEditorAttributesIfAvailable();
    this.createComponentInstance().subscribe((component) => {
      this.eventListener.forEach((element) => {
        component.addEventListener(element.type, element.listener);
      });
    });
  }

  ngAfterViewInit() {
    this.abcComponentService.addInstance(this);
  }

  ngOnDestroy(): void {
    this.eventListener = [];
    this.abcComponentService.removeInstance(this);
  }

  createComponentInstance(): Observable<any> {
    const observable = new Observable((observer) => {
      switch (this.style) {
        case ComponentStyle.Material: {
          //do specific stuff for Material
          break;
        }
        case ComponentStyle.DevExpress: {
          this.componentRef = this.abcComponentService.loadComponent(
            DevExEditorComponent,
            this.viewContainerRef
          ).componentRef.instance;
          this.componentRef.value = this.value ? this.value : '';
          this.componentRef.validationStatus = this.validationStatus ? this.validationStatus : 'valid';
          this.componentRef.showToolbar = this.showToolbar == false ? this.showToolbar : true;
          this.componentRef.placeholder = this.placeholder;
          this.componentRef.displayIconEdit = this.displayIconEdit;
          this.componentRef.saveIcon = this.saveIcon;
          this.componentRef.class = this.class;
          this.componentRef.readOnly = this.readOnly;
          this.componentRef.stylingMode = this.stylingMode ? this.stylingMode : this.abcComponentService.stylingMode;
          this.componentRef.rtlEnabled = this.rtlEnabled ? this.rtlEnabled : false;
          this.componentRef.height = this.height ? this.height : '100%';
          this.componentRef.width = this.width;
          this.componentRef.filterToolbarItems = this.filterToolbarItems;
          if (this._identifier) this.componentRef.identifier = this._identifier;
          this.componentRef.onValueChanged.subscribe((e) => {
            this.valueChange(e);
          });
          this.componentRef.onEdit.subscribe((event) => {
            this.onTextEdit(event);
          });
          this.componentRef.onSaveClick.subscribe((event) => {
            this.saveClick(event);
          });
          break;
        }
      }
      observer.next(this.componentRef);
    });
    return observable;
  }

  setEditorAttributesIfAvailable(): void {
    if (this.attrId) {
      const style: EditorStyle = {
        height: this.height ? this.height : '100%',
        width: this.width ? this.width : '100%',
      };
      this.htmlAttributes = {
        id: this.attrId,
        disabled: this.disabled,
        activeStateEnabled: this.activeStateEnabled,
        focusStateEnabled: this.focusStateEnabled,
        hoverStateEnabled: this.hoverStateEnabled,
        displayEdit: this.displayEdit,
        showToolbar: this.showToolbar,
        isValid: this.isValid,
        name: this.name,
        placeholder: this.placeholder,
        readOnly: this.readOnly,
        rtlEnabled: this.rtlEnabled ? this.rtlEnabled : false,
        tabIndex: this.tabIndex,
        stylingMode: this.stylingMode,
        saveIcon: this.saveIcon,
        validationError: this.validationError,
        validationErrors: this.validationErrors,
        validationMessageMode: this.validationMessageMode,
        validationStatus: this.validationStatus,
        value: this.value,
        visible: this.visible,
        class: this.class,
        tools: this.attrTools,
        setEditorStyle: (_editorStyle: EditorStyle) => {
          _editorStyle = style;
        },
      };
    }
  }

  setText(val: string): void {
    this.componentRef.setText(val);
  }
  private onTextEdit(e) {
    this.onEdit.emit({ value: e });
  }
  private saveClick(e) {
    this.onSaveClick.emit(e);
  }
  valueChanged(): boolean {
    return this.componentRef.valueChanged();
  }

  getText(): string {
    return this.componentRef.getText();
  }

  getPlainText(): string {
    return this.componentRef.getPlainText();
  }

  setIdentifier(identifier: string) {
    this._identifier = identifier;
  }

  getIdentifier(): string {
    return this.componentRef.getIdentifier();
  }

  valueChange(e): void {
    this.onValueChanged.emit(e);
  }
}
